/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { ColumnInstance } from 'react-table';
import { inputFormatters } from '@blend/shared-react';
import { NotaryAPI } from '../../../../api/notary/types';
import { ParticipantActions } from './ParticipantActions';
import { ClosingPartyTypeToRoleName } from './ContactList';
import { TextTableCell } from '@components/Table/TextTableCell';
import { TableHeader } from '@components/Table/TableHeader';
import { formatFullName } from '@utils/formatting';

const ActionCell: React.FC<{
  participant: ParticipantDataRow;
}> = ({ participant }) => {
  return <ParticipantActions participant={participant} />;
};

interface BaseColumnConfig {
  Cell: React.FC<{ value: string }>;
  Header: React.FC<{ column: ColumnInstance<any> }>;
}

const baseColumnConfig: BaseColumnConfig = {
  Cell: TextTableCell,
  Header: TableHeader,
};

export type ParticipantDataRow = NotaryAPI.Participant & { allTitles: string };

export const participantsColumnConfigurations = () => [
  {
    ...baseColumnConfig,
    accessor: (participant: ParticipantDataRow) => formatFullName(participant),
    id: 'Name',
    width: '20%',
  },
  {
    ...baseColumnConfig,
    accessor: (participant: ParticipantDataRow) => participant.allTitles,
    id: 'Role',
    width: '17%',
  },
  {
    ...baseColumnConfig,
    accessor: (participant: ParticipantDataRow) => participant.email,
    id: 'Email Address',
    width: '20%',
  },
  {
    ...baseColumnConfig,
    accessor: (participant: ParticipantDataRow) =>
      participant.phoneNumber ? inputFormatters.PHONE.format(participant.phoneNumber) : participant.phoneNumber,
    id: 'Phone Number',
    width: '15%',
  },
  {
    ...baseColumnConfig,
    accessor: (participant: ParticipantDataRow) => (participant.requiresKba ? 'Enabled' : 'Disabled'),
    id: 'Security questions',
    width: '8%',
  },
  {
    ...baseColumnConfig,
    accessor: (participant: ParticipantDataRow) => participant,
    Cell: ({ value }: { value: ParticipantDataRow }) => <ActionCell participant={value} />,
    id: 'Actions',
    width: '20%',
  },
];

export const contactsColumnConfigurations = [
  {
    ...baseColumnConfig,
    accessor: (closingParty: NotaryAPI.ClosingParty) => `${closingParty.firstName} ${closingParty.lastName}`,
    id: 'Name',
    width: '20%',
  },
  {
    ...baseColumnConfig,
    accessor: (closingParty: NotaryAPI.ClosingParty) =>
      closingParty.type ? ClosingPartyTypeToRoleName.get(closingParty.type) : closingParty.type,
    id: 'Role',
    width: '20%',
  },
  {
    ...baseColumnConfig,
    accessor: (closingParty: NotaryAPI.ClosingParty) => closingParty.email,
    id: 'Email Address',
    width: '25%',
  },
  {
    ...baseColumnConfig,
    accessor: (closingParty: NotaryAPI.ClosingParty) =>
      closingParty.phoneNumber ? inputFormatters.PHONE.format(closingParty.phoneNumber) : closingParty.phoneNumber,
    id: 'Phone Number',
    width: '15%',
  },
  {
    ...baseColumnConfig,
    accessor: (closingParty: NotaryAPI.ClosingParty) =>
      closingParty.type === NotaryAPI.ClosingPartyType.CLOSER ? 'N/A' : closingParty.agencyName,
    id: 'Agency',
    width: '20%',
  },
];
